import { useState, useMemo } from "react";

// ─────────────────────────────────────────────────────────────
// ROAS Profit Checker
// Enter your real numbers + the ROAS you're actually getting.
// Get one answer: profitable or not, and your break-even ROAS.
// ─────────────────────────────────────────────────────────────

const INR  = (n) => "₹" + Math.round(n || 0).toLocaleString("en-IN");
const num  = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));

const S = {
  wrap:  { maxWidth: 980, margin: "0 auto" },
  card:  { background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb", overflow: "hidden" },
  phead: { background: "#0F1A0F", color: "#fff", padding: "13px 18px", fontWeight: 700, fontSize: 14, display: "flex", justifyContent: "space-between", alignItems: "center" },
  ptag:  { fontSize: 10, color: "#F47920", textTransform: "uppercase", letterSpacing: "1px" },
  stitle:{ background: "#fef6e0", color: "#92670a", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", padding: "6px 18px" },
  row:   { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 18px", fontSize: 13, borderBottom: "1px solid #f5f0e8" },
  lbl:   { color: "#3d3020", flex: 1 },
  unit:  { color: "#b8a890", fontSize: 11, marginLeft: 4 },
  input: { width: 104, textAlign: "right", border: "1px solid #e0d5c8", borderRadius: 6, padding: "6px 8px", fontSize: 13, fontFamily: "inherit", background: "#fffdf8", color: "#0F1A0F", fontWeight: 600 },
  roasInput: { width: 104, textAlign: "right", border: "2px solid #F47920", borderRadius: 6, padding: "6px 8px", fontSize: 14, fontFamily: "inherit", background: "#fff7ed", color: "#0F1A0F", fontWeight: 700 },
  val:   { fontWeight: 700, fontSize: 13, color: "#0F1A0F", minWidth: 104, textAlign: "right" },
};

// Stable input component (defined outside so React keeps cursor focus)
function Num({ id, value, onChange, big }) {
  return (
    <input
      style={big ? S.roasInput : S.input}
      type="text"
      inputMode="decimal"
      value={value}
      onChange={(e) => {
        const v = e.target.value.replace(/[^0-9.]/g, "");
        onChange(id, v);
      }}
    />
  );
}

// Verdict banner — profitable or losing money
function Verdict({ profit, breakevenRoas, currentRoas, unit }) {
  const ok = profit > 0;
  return (
    <div style={{ background: ok ? "#0f2818" : "#2a0f0f", borderRadius: 12, padding: "24px 28px", marginTop: 18, textAlign: "center", border: "1px solid " + (ok ? "#1d5036" : "#5c1d1d") }}>
      <div style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 8 }}>
        At {currentRoas}x ROAS, you are
      </div>
      <div style={{ fontSize: 40, fontWeight: 800, color: ok ? "#4ade80" : "#f87171", marginBottom: 6 }}>
        {ok ? "PROFITABLE ✓" : "LOSING MONEY ✗"}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: "#fff", marginBottom: 14 }}>
        {ok ? "+" : ""}{INR(profit)} {unit}
      </div>
      <div style={{ display: "inline-block", background: "rgba(255,255,255,0.1)", borderRadius: 8, padding: "10px 18px", fontSize: 14, color: "#fff" }}>
        Your break-even ROAS is <strong style={{ color: "#F47920" }}>{breakevenRoas.toFixed(2)}x</strong>
        {" — "}
        {currentRoas >= breakevenRoas
          ? <span style={{ color: "#4ade80" }}>you're above it, keep going 🚀</span>
          : <span style={{ color: "#f87171" }}>you need to get above this to make money</span>}
      </div>
    </div>
  );
}

export default function PageCalculator({ toast, tenant, onUpgrade }) {
  const [tab, setTab] = useState("product");

  // PER PRODUCT
  const [P, setP] = useState({ price: 999, gst: 18, cogs: 275, ship: 50, txnPct: 2, retPct: 10, roas: 4 });
  // WHOLE BUSINESS
  const [B, setB] = useState({ rev: 500000, gst: 18, cogs: 200000, other: 100000, roas: 3.5 });

  const setProd = (k, v) => setP((s) => ({ ...s, [k]: v }));
  const setBiz  = (k, v) => setB((s) => ({ ...s, [k]: v }));

  // ── PER PRODUCT calc ──
  const prod = useMemo(() => {
    const price = num(P.price);
    const priceNoGst = price / (1 + num(P.gst) / 100);
    const retCost = priceNoGst * num(P.retPct) / 100;
    const txnCost = price * num(P.txnPct) / 100;
    const marginBeforeAds = priceNoGst - num(P.cogs) - num(P.ship) - txnCost - retCost;
    const adCost = num(P.roas) > 0 ? price / num(P.roas) : 0;
    const profit = marginBeforeAds - adCost;
    const breakevenRoas = marginBeforeAds > 0 ? price / marginBeforeAds : 0;
    // ROAS needed to hit a target NET profit % (of selling price)
    const roasForProfit = (pct) => {
      const denom = marginBeforeAds - price * pct;
      return denom > 0 ? price / denom : null;
    };
    const profitLadder = [0.10, 0.15, 0.20, 0.25].map((p) => ({ pct: p * 100, roas: roasForProfit(p) }));
    return { priceNoGst, retCost, marginBeforeAds, adCost, profit, breakevenRoas, profitLadder };
  }, [P]);

  // ── WHOLE BUSINESS calc ──
  const biz = useMemo(() => {
    const rev = num(B.rev);
    const revNoGst = rev / (1 + num(B.gst) / 100);
    const marginBeforeAds = revNoGst - num(B.cogs) - num(B.other);
    const adSpend = num(B.roas) > 0 ? rev / num(B.roas) : 0;
    const profit = marginBeforeAds - adSpend;
    const breakevenRoas = marginBeforeAds > 0 ? rev / marginBeforeAds : 0;
    return { revNoGst, marginBeforeAds, adSpend, profit, breakevenRoas };
  }, [B]);


  return (
    <div style={S.wrap}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.5px", color: "#0F1A0F" }}>🎯 ROAS Profit Checker</div>
        <div style={{ color: "#8a7a6a", fontSize: 13, marginTop: 2 }}>Enter your numbers and the ROAS you're actually getting. Find out instantly if you're making money or losing it.</div>
      </div>

      {/* Upgrade nudge for free-plan users */}
      {tenant?.plan === "free" && (
        <div style={{ background: "linear-gradient(90deg,#0F1A0F,#1f3320)", borderRadius: 12, padding: "16px 20px", marginBottom: 18, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: 14, marginBottom: 2 }}>💡 This calculator shows your <em>target</em> ROAS. Want to see your <em>actual</em> ROAS automatically?</div>
            <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 12 }}>Upgrade to sync your Myntra/Amazon orders and 3June calculates real per-order profit for you.</div>
          </div>
          <button onClick={() => onUpgrade && onUpgrade()} style={{ background: "#F47920", color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", flexShrink: 0 }}>
            Upgrade →
          </button>
        </div>
      )}

      {/* Tab switch */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {[["product", "📦 Per Product"], ["business", "🏢 Whole Business"]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} style={{
            padding: "9px 18px", borderRadius: 8, border: "1px solid " + (tab === id ? "#F47920" : "#e5e7eb"),
            background: tab === id ? "#fff7ed" : "#fff", color: tab === id ? "#F47920" : "#6b7280",
            fontWeight: tab === id ? 700 : 500, fontSize: 13, cursor: "pointer", fontFamily: "inherit",
          }}>{label}</button>
        ))}
      </div>

      {/* ── PER PRODUCT ── */}
      {tab === "product" && (
        <div>
          <div style={S.card}>
            <div style={S.phead}><span>📦 One Product</span><span style={S.ptag}>Per sale</span></div>

            <div style={S.stitle}>What you sell it for</div>
            <div style={S.row}><span style={S.lbl}>Selling price <span style={S.unit}>(incl. GST)</span></span><Num id="price" value={P.price} onChange={setProd} /></div>
            <div style={S.row}><span style={S.lbl}>GST rate <span style={S.unit}>%</span></span><Num id="gst" value={P.gst} onChange={setProd} /></div>
            <div style={S.row}><span style={S.lbl}>Price after removing GST</span><span style={S.val}>{INR(prod.priceNoGst)}</span></div>

            <div style={S.stitle}>What it costs you (₹ per unit)</div>
            <div style={S.row}><span style={S.lbl}>Product cost (COGS) <span style={S.unit}>₹</span></span><Num id="cogs" value={P.cogs} onChange={setProd} /></div>
            <div style={S.row}><span style={S.lbl}>Shipping &amp; packing <span style={S.unit}>₹</span></span><Num id="ship" value={P.ship} onChange={setProd} /></div>
            <div style={S.row}><span style={S.lbl}>Transaction / platform fee <span style={S.unit}>% of price</span></span><Num id="txnPct" value={P.txnPct} onChange={setProd} /></div>
            <div style={S.row}><span style={S.lbl}>Return rate <span style={S.unit}>%</span></span><Num id="retPct" value={P.retPct} onChange={setProd} /></div>
            <div style={{ ...S.row, background: "#fffaf0" }}><span style={S.lbl}><strong>Margin before ads</strong></span><span style={{ ...S.val, fontSize: 15, color: prod.marginBeforeAds >= 0 ? "#2A7A4E" : "#dc2626" }}>{INR(prod.marginBeforeAds)}</span></div>

            <div style={S.stitle}>Your actual ads</div>
            <div style={{ ...S.row, background: "#fff7ed" }}><span style={S.lbl}><strong>ROAS you're getting</strong> <span style={S.unit}>(revenue ÷ ad spend)</span></span><Num id="roas" value={P.roas} onChange={setProd} big /></div>
            <div style={S.row}><span style={S.lbl}>Ad cost per sale <span style={S.unit}>(price ÷ ROAS)</span></span><span style={S.val}>{INR(prod.adCost)}</span></div>
          </div>

          <Verdict profit={prod.profit} breakevenRoas={prod.breakevenRoas} currentRoas={num(P.roas)} unit="profit per sale" />

          {/* Profit-target ROAS ladder */}
          <div style={{ ...S.card, marginTop: 18 }}>
            <div style={S.phead}><span>📈 ROAS needed for profit</span><span style={S.ptag}>Net margin targets</span></div>
            <div style={{ ...S.row, background: "#f9fafb", fontWeight: 700, fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              <span style={S.lbl}>If you want this net profit</span><span style={{ minWidth: 104, textAlign: "right" }}>You need this ROAS</span>
            </div>
            <div style={{ ...S.row }}><span style={S.lbl}>Just break even <span style={S.unit}>(0% profit)</span></span><span style={{ ...S.val, color: "#F47920" }}>{prod.breakevenRoas > 0 ? prod.breakevenRoas.toFixed(2) + "x" : "—"}</span></div>
            {prod.profitLadder.map((t) => (
              <div key={t.pct} style={S.row}>
                <span style={S.lbl}>{t.pct}% net profit</span>
                <span style={{ ...S.val, color: t.roas ? "#2A7A4E" : "#dc2626" }}>{t.roas ? t.roas.toFixed(2) + "x" : "Not possible"}</span>
              </div>
            ))}
            <div style={S.note}>Your ads must hit at least the break-even ROAS to avoid losing money. To actually take home profit, aim for the higher targets. "Not possible" means your margin is too thin to reach that profit % at any ROAS — cut costs or raise price.</div>
          </div>
        </div>
      )}

      {/* ── WHOLE BUSINESS ── */}
      {tab === "business" && (
        <div>
          <div style={S.card}>
            <div style={S.phead}><span>🏢 Whole Business</span><span style={S.ptag}>This period</span></div>

            <div style={S.stitle}>Revenue</div>
            <div style={S.row}><span style={S.lbl}>Total revenue <span style={S.unit}>(incl. GST)</span></span><Num id="rev" value={B.rev} onChange={setBiz} /></div>
            <div style={S.row}><span style={S.lbl}>GST rate <span style={S.unit}>%</span></span><Num id="gst" value={B.gst} onChange={setBiz} /></div>
            <div style={S.row}><span style={S.lbl}>Revenue after removing GST</span><span style={S.val}>{INR(biz.revNoGst)}</span></div>

            <div style={S.stitle}>Your costs (₹ total)</div>
            <div style={S.row}><span style={S.lbl}>Product cost (COGS) <span style={S.unit}>₹</span></span><Num id="cogs" value={B.cogs} onChange={setBiz} /></div>
            <div style={S.row}><span style={S.lbl}>Everything else <span style={S.unit}>(shipping, fees, returns, salaries, rent)</span></span><Num id="other" value={B.other} onChange={setBiz} /></div>
            <div style={{ ...S.row, background: "#fffaf0" }}><span style={S.lbl}><strong>Margin before ads</strong></span><span style={{ ...S.val, fontSize: 15, color: biz.marginBeforeAds >= 0 ? "#2A7A4E" : "#dc2626" }}>{INR(biz.marginBeforeAds)}</span></div>

            <div style={S.stitle}>Your actual ads</div>
            <div style={{ ...S.row, background: "#fff7ed" }}><span style={S.lbl}><strong>Blended ROAS you're getting</strong> <span style={S.unit}>(total revenue ÷ total ad spend)</span></span><Num id="roas" value={B.roas} onChange={setBiz} big /></div>
            <div style={S.row}><span style={S.lbl}>Total ad spend <span style={S.unit}>(revenue ÷ ROAS)</span></span><span style={S.val}>{INR(biz.adSpend)}</span></div>
          </div>

          <Verdict profit={biz.profit} breakevenRoas={biz.breakevenRoas} currentRoas={num(B.roas)} unit="profit this period" />
        </div>
      )}
    </div>
  );
}
